import {
  RegisterCompletionUserResponse,
  RegistrationCompletionFormValues,
} from '../types/schemas/registerCompletionSchema';
import axios from 'axios';
import { store } from '../redux/store';
import {
  CiamToImsGenderCodeMapping,
  GENDER_CODE_CIAM,
  OptInState,
  PatchImsUserRequest,
  RegistrationFlowFormValues,
  SaveOptInsRequest,
} from '../types/registrationFlows';
import { RegistrationFlowOrchestration } from '../types/apiConfig';
import { PatchRequestBuilder } from '../utils/patchRequestBuilder';

class RegistrationCompletionService {
  public async completeDefaultRegistration(
    identityId: string,
    accessToken: string,
    values: RegistrationCompletionFormValues
  ): Promise<RegisterCompletionUserResponse> {
    const data: PatchImsUserRequest = {};

    if (values.birthDateDay || values.birthDateMonth || values.birthDateYear) {
      data.demographicsPerson = {
        ...data.demographicsPerson,
        dateOfBirth: this.formatBirthDate(
          String(values.birthDateDay),
          String(values.birthDateMonth),
          String(values.birthDateYear)
        ),
      };
    }

    if (values.genderType) {
      data.demographicsPerson = {
        ...data.demographicsPerson,
        genderCode: CiamToImsGenderCodeMapping[values.genderType as unknown as GENDER_CODE_CIAM],
        ...(values.genderCustom !== undefined &&
          values.genderType?.toLowerCase() === GENDER_CODE_CIAM.OTHER.toLowerCase() && {
            genderCustom: values.genderCustom,
          }),
      };
    }

    if (Object.keys(data).length !== 0) {
      return await this.patchImsIdentity(identityId, accessToken, data);
    } else {
      return {} as RegisterCompletionUserResponse;
    }
  }

  public async completeRegistrationFlow(
    identityId: string,
    accessToken: string,
    values: RegistrationFlowFormValues,
    orchestration: RegistrationFlowOrchestration
  ): Promise<unknown> {
    return await this.completeImsRegistrationFlow(identityId, accessToken, values, orchestration);
  }

  public async sendUserPreferences(
    identityId: string,
    email: string,
    brand: string,
    accessToken: string,
    values: Array<OptInState>
  ): Promise<void> {
    if (values.length === 0) {
      return;
    }

    const data: SaveOptInsRequest = {
      emailAddress: email,
      brandCode: brand,
      optInCodes: values,
    };
    // FusionAuth user tokens are only accepted on v4.
    return await axios.post(
      `${this.getApiConfig().apiRoot}/v4/identities/${identityId}/optIn`,
      data,
      {
        headers: {
          Authorization: `bearer ${accessToken}`,
        },
      }
    );
  }

  private getApiConfig = () => store.getState().apiConfig;

  private formatBirthDate(day: string, month: string, year: string): string | null {
    if (day === '' || month === '' || year === '') return null;
    return `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
  }

  private async completeImsRegistrationFlow(
    identityId: string,
    accessToken: string,
    values: RegistrationFlowFormValues,
    orchestration: RegistrationFlowOrchestration
  ): Promise<PatchImsUserRequest> {
    const builder = new PatchRequestBuilder(orchestration)
      .withFirstName(values.firstName)
      .withLastName(values.lastName)
      .withGender(values.gender)
      .withGenderCustom(values.genderOther)
      .withBirthDate(values.birthDateDay, values.birthDateMonth, values.birthDateYear)
      .withPhoneNumber(values.telephone)
      .withCity(values.address.city)
      .withPostalCode(values.address.postalCode)
      .withStreet(values.address.street)
      .withBoxNumber(values.address.box)
      .withHouseNumber(values.address.houseNumber)
      .withCountry(values.country);

    const data = builder.asPatchImsUserRequest();
    return await this.patchImsIdentity(identityId, accessToken, data);
  }

  private async patchImsIdentity(
    identityId: string,
    accessToken: string,
    data: PatchImsUserRequest
  ): Promise<PatchImsUserRequest> {
    const ims = this.getApiConfig().ims;
    return axios
      .patch<PatchImsUserRequest>(
        `${ims.apiRoot}/${ims.fusionAuthVersion}/api/identities/${identityId}`,
        data,
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        }
      )
      .then((r) => r.data)
      .catch((e) => {
        console.error(e);
        throw e;
      });
  }
}

export const registrationCompletionService = new RegistrationCompletionService();
